// Stock control API contracts for the Admin (M4, WP4.1): the approvals inbox, stock counts,
// write-offs (list and report), the daily state list, and the approval / state-list
// settings. Wire format as elsewhere: snake_case fields, quantities as decimal strings,
// money as integer cents, instants as ISO-8601 UTC strings, days as `YYYY-MM-DD`.
//
//   GET  /approvals                      the inbox (default: open requests), filterable
//   GET  /approvals/:id                  one request
//   POST /approvals/:id/decision         approve / reject / cancel, with a reason
//   GET  /counts, GET /counts/:id        counts; the detail has variance and value per line
//   GET  /write-offs                     write-offs, filterable
//   GET  /write-offs/report              totals in cents by reason, product or day
//   GET  /state-tasks                    a day's state list per branch (done / pending)
//   POST /state-tasks/generate           make (or top up) today's list for a branch now
//   GET  /settings/stock-control         approval thresholds, state-list time
//   PUT  /settings/stock-control         change them
//
// Reads need `reports.view` (within the caller's branch scope). Deciding needs the
// approver's permission (`writeoff.approve` for write-offs, `stock.adjust` for count
// adjustments); making a list needs `stock.adjust`; the settings need `writeoff.approve`
// and all branches.
import { z } from 'zod';
import { stateKeySchema } from '../catalogue/index.js';
import { MAX_STATION_AUTO_SIGN_OUT_MINUTES } from '../devices/index.js';
import { positiveQtyString, qtyString } from '../quantity/index.js';
import {
  APPROVAL_DECISIONS,
  APPROVAL_MODES,
  APPROVAL_STATUSES,
  APPROVAL_TYPES,
} from '../stock-control/approvals.js';
import { WRITE_OFF_REASONS } from '../stock-control/writeoffs.js';
import { isCutoff, isPlainDate } from '../time/index.js';
import {
  STOCK_PAGE_DEFAULT,
  STOCK_PAGE_MAX,
  stockLocationRefSchema,
  stockProductRefSchema,
} from './stock.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const cents = z.int();
const count = z.int().min(0);
const limit = z.coerce.number().int().min(1).max(STOCK_PAGE_MAX).default(STOCK_PAGE_DEFAULT);
const cursor = z.string().min(1).max(512).optional();
const queryBool = z
  .enum(['true', 'false'])
  .transform((v) => v === 'true')
  .optional();

/** `:id` path parameter of the stock-control routes. */
export const stockControlIdParamsSchema = z.object({ id: z.uuid() });

/** A person in stock-control responses: a staff member or an admin user. */
export const personRefSchema = z.object({
  kind: z.enum(['staff', 'admin']),
  id: z.uuid(),
  /** Display name, when known. */
  name: z.string().nullable(),
});
/** A person reference. */
export type PersonRef = z.infer<typeof personRefSchema>;

/** A lot reference. */
export const lotRefSchema = z.object({
  id: z.uuid(),
  lot_code: z.string(),
  best_before: plainDate.nullable(),
});

/** A branch reference. */
export const branchRefSchema = z.object({ id: z.uuid(), name: z.string() });

// ---------------------------------------------------------------------------------------
// Approvals

/** A count reference: the count that covers a row. */
export const countRefSchema = z.object({ id: z.uuid(), number: z.string(), counted_at: instant });

/**
 * One row (product, lot, state) of what a rejection writes (UI4A-201): per lot, because a
 * later count covers rows, not products (D-055). Waiting: what rejecting now would do.
 * Rejected: what the rejection did.
 */
export const reversalRowSchema = z.object({
  lot: z.object({ id: z.uuid(), lot_code: z.string() }).nullable(),
  state: z.string(),
  /**
   * The correction, signed as it moves stock: `+2` puts 2 back on the books (a loss
   * reversed), `-4` takes 4 off again (a gain reversed).
   */
  qty: qtyString,
  /** qty × the recorded standard cost, signed like qty; null without a cost. */
  value_cents: cents.nullable(),
  /** True when it is (would be) reversed; false when a later count covers it. */
  reversed: z.boolean(),
  /** The later count covering the row (why it isn't reversed), or null. */
  counted_since: countRefSchema.nullable(),
});
/** A row of a rejection. */
export type ReversalRow = z.infer<typeof reversalRowSchema>;

/** What a rejection does (did) to one product of an approval, per lot and in total. */
export const lineReversalSchema = z.object({
  rows: z.array(reversalRowSchema),
  /** Σ qty of the reversed rows (signed, as in the rows). */
  reversed_qty: qtyString,
  /** Σ value of the reversed rows; null when the product has no cost. */
  reversed_value_cents: cents.nullable(),
  /** Σ qty of the rows a later count covers: left as counted. */
  not_reversed_qty: qtyString,
  not_reversed_value_cents: cents.nullable(),
});
/** A line's reversal. */
export type LineReversal = z.infer<typeof lineReversalSchema>;

/**
 * What a rejection does (did) as a whole: `reversed` every row, `partly_reversed` some (a
 * later count covers the rest), `not_reversed` none (later counts cover all of it).
 */
export const REVERSAL_OUTCOMES = ['reversed', 'partly_reversed', 'not_reversed'] as const;
/** A rejection's outcome. */
export type ReversalOutcome = (typeof REVERSAL_OUTCOMES)[number];

/** A rejection's totals over every line (only what is actually reversed counts as reversed). */
export const reversalSummarySchema = z.object({
  outcome: z.enum(REVERSAL_OUTCOMES),
  /** Σ value of the reversed rows with a cost (signed: + puts stock value back). */
  reversed_value_cents: cents,
  reversed_rows: count,
  /** Reversed rows whose product has no cost (not in the value). */
  reversed_uncosted_rows: count,
  not_reversed_value_cents: cents,
  not_reversed_rows: count,
  not_reversed_uncosted_rows: count,
});
/** A rejection's totals. */
export type ReversalSummary = z.infer<typeof reversalSummarySchema>;

/** One product's part of an approval: what changed and why it needed approval. */
export const approvalLineSchema = z.object({
  product: stockProductRefSchema,
  /** The change (a count's net adjustment, signed; a write-off's quantity, positive). */
  qty: qtyString,
  level_before: qtyString,
  /** |qty| × standard cost; null when the product has no cost. */
  value_cents: cents.nullable(),
  /**
   * The cost per base unit the value was judged by (the standard cost recorded with the
   * count or write-off); null when the product had none.
   */
  unit_cost_cents: cents.nullable(),
  /** |qty| ÷ level before × 100; null when the level before was not above zero. */
  percent: z.string().nullable(),
  /** `value`, `percent`, `cannot_judge` (no cost and no level). */
  grounds: z.array(z.string()),
  /** The product had no standard cost: judged by percentage only. */
  no_cost: z.boolean(),
  /**
   * What rejecting does (waiting) or did (rejected) to this product, per lot (UI4A-201);
   * null when approved or closed.
   */
  reversal: lineReversalSchema.nullable(),
});

/**
 * A row of a request that a count dated **after** it covers (D-055): rejecting doesn't
 * reverse it — the count already settled that level ("not reversed: counted since").
 */
export const laterCountRowSchema = z.object({
  product: stockProductRefSchema,
  lot: z.object({ id: z.uuid(), lot_code: z.string() }).nullable(),
  state: z.string(),
  count: countRefSchema,
});
/** A row a later count covers. */
export type LaterCountRow = z.infer<typeof laterCountRowSchema>;

/** An approval request (async or manager PIN), as the Admin sees it. */
export const approvalSchema = z.object({
  id: z.uuid(),
  type: z.enum(APPROVAL_TYPES),
  mode: z.enum(APPROVAL_MODES),
  status: z.enum(APPROVAL_STATUSES),
  /** What it is about: `stock_count` or `write_off` (with the record's number). */
  subject: z.object({ type: z.string(), id: z.uuid(), number: z.string().nullable() }),
  branch: branchRefSchema.nullable(),
  /** Where the stock was (the count's or write-off's location), when known. */
  location: stockLocationRefSchema.nullable(),
  requested_by: personRefSchema.nullable(),
  requested_at: instant,
  decided_by: personRefSchema.nullable(),
  decided_at: instant.nullable(),
  /** The decision's reason (required to reject or cancel). */
  reason: z.string().nullable(),
  /** Σ value of the lines that needed approval; null when none had a cost. */
  value_cents: cents.nullable(),
  lines: z.array(approvalLineSchema),
  /**
   * Rows a later count covers (D-055). Waiting: those a rejection now would **not**
   * reverse (it needs `acknowledge_later_counts`). Rejected: those it did not reverse.
   * Otherwise empty.
   */
  later_counts: z.array(laterCountRowSchema),
  /**
   * What rejecting does (waiting: now) or did (rejected), over every line; null when
   * approved or closed.
   */
  reversal: reversalSummarySchema.nullable(),
});
/** An approval request. */
export type Approval = z.infer<typeof approvalSchema>;

/** `GET /approvals` query. Newest first; `status` defaults to open requests. */
export const approvalsQuerySchema = z.object({
  status: z.enum(APPROVAL_STATUSES).default('requested'),
  type: z.enum(APPROVAL_TYPES).optional(),
  branch: z.uuid().optional(),
  limit,
  cursor,
});
/** Parsed `GET /approvals` query. */
export type ApprovalsQuery = z.infer<typeof approvalsQuerySchema>;

/** `GET /approvals` response. */
export const approvalsResponseSchema = z.object({
  items: z.array(approvalSchema),
  next_cursor: z.string().nullable(),
});
/** Response of `GET /approvals`. */
export type ApprovalsResponse = z.infer<typeof approvalsResponseSchema>;

/**
 * `POST /approvals/:id/decision` body. `approve` confirms what was applied; `reject`
 * reverses it with compensating movements (audited), except rows a later count covers
 * (D-055): when there are any, the manager must say they saw that by naming the covering
 * counts they were shown (`acknowledge_later_counts`, their ids), else 409 `later_count`
 * with `later_counts` (the counts) and `not_reversed`; when the counts found at decision
 * time differ from those named, 409 `later_counts_changed` (read it again, D-059). A row
 * covered only by a count whose own approval is still waiting: 409
 * `covering_count_pending` with `pending_counts` (decide that count first, D-059).
 * `cancel` closes a request that needs no decision (the record stays as applied). Reject
 * and cancel need a reason.
 */
export const approvalDecisionRequestSchema = z
  .object({
    decision: z.enum(APPROVAL_DECISIONS),
    reason: z.string().trim().max(500).optional(),
    acknowledge_later_counts: z.array(z.uuid()).max(500).optional(),
    /**
     * A purchase request only (M6), with `approve`: each product judged on its own —
     * approved (optionally with another number of packs) or rejected. Products not listed
     * are approved as requested; at least one must stay approved (else reject). Approving
     * makes the purchase order from the approved products.
     */
    purchase_lines: z
      .array(
        z.object({
          product_id: z.uuid(),
          decision: z.enum(['approve', 'reject']),
          qty_packs: positiveQtyString.optional(),
        }),
      )
      .max(500)
      .optional(),
  })
  .refine((b) => b.decision === 'approve' || (b.reason ?? '') !== '', {
    message: 'Give a reason',
    path: ['reason'],
  })
  .refine((b) => b.purchase_lines === undefined || b.decision === 'approve', {
    message: 'Products are judged when approving',
    path: ['purchase_lines'],
  })
  .refine(
    (b) =>
      b.purchase_lines === undefined ||
      new Set(b.purchase_lines.map((l) => l.product_id)).size === b.purchase_lines.length,
    { message: 'Judge each product once', path: ['purchase_lines'] },
  );
/** Body of `POST /approvals/:id/decision`. */
export type ApprovalDecisionRequest = z.infer<typeof approvalDecisionRequestSchema>;

/** `POST /approvals/:id/decision` response: the request after the decision. */
export const approvalDecisionResponseSchema = approvalSchema.extend({
  /** Compensating movements written by a rejection (empty otherwise). */
  compensation_movement_ids: z.array(z.uuid()),
  /** The purchase order an approved purchase request made (absent for other types). */
  purchase_order_id: z.uuid().nullable().optional(),
});
/** Response of `POST /approvals/:id/decision`. */
export type ApprovalDecisionResponse = z.infer<typeof approvalDecisionResponseSchema>;

// ---------------------------------------------------------------------------------------
// Counts

/** A reference to an approval from the record it approves. */
export const approvalRefSchema = z.object({
  id: z.uuid(),
  mode: z.enum(APPROVAL_MODES),
  status: z.enum(APPROVAL_STATUSES),
  /** A rejection's outcome (UI4A-202): what it actually reversed; null unless rejected. */
  reversal: z.enum(REVERSAL_OUTCOMES).nullable(),
});

/** A stock count in lists. */
export const countSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  branch: branchRefSchema,
  location: stockLocationRefSchema.nullable(),
  scope: z.enum(['location', 'products', 'product', 'lot', 'full']),
  blind: z.boolean(),
  counted_at: instant,
  counted_by: personRefSchema.nullable(),
  /**
   * `applied`; `superseded` when a later count of every row was recorded first (B-035:
   * nothing was adjusted).
   */
  status: z.string(),
  /** Some adjustment was above the threshold without a valid manager-PIN approval. */
  needs_approval: z.boolean(),
  approval: approvalRefSchema.nullable(),
  line_count: count,
  /**
   * Lines where counted ≠ system as the ledger holds them now: superseded lines and lines a
   * rejection reversed are left out, here and in the next three (B-087).
   */
  variance_count: count,
  /** Σ (counted − system) × cost over costed lines (signed: a loss is negative). */
  net_value_cents: cents,
  /** Σ |counted − system| × cost over costed lines. */
  gross_value_cents: cents,
  /** Lines with a variance whose product has no cost (not in the values above). */
  uncosted_variance_count: count,
  /**
   * Superseded lines where counted ≠ system: the difference was noted, not booked (a later
   * count of the same stock was recorded first, B-035). Never in the values above.
   */
  noted_variance_count: count,
  /**
   * Lines where counted ≠ system whose adjustment a rejection reversed (B-087): booked, then
   * undone. Never in the values above.
   */
  reversed_variance_count: count,
  /** Σ (counted − system) × cost over the reversed, costed lines (signed). */
  reversed_net_value_cents: cents,
});
/** A count in lists. */
export type CountSummary = z.infer<typeof countSummarySchema>;

/** One line of a count, with its variance and value. */
export const countLineDetailSchema = z.object({
  product: stockProductRefSchema,
  lot: lotRefSchema.nullable(),
  state: stateKeySchema,
  /** `counted`, `not_counted` (in scope, no line: counted as 0), `new_lot` (unlabelled). */
  line_kind: z.enum(['counted', 'not_counted', 'new_lot']),
  counted_qty: qtyString,
  /** The ledger level as of the count time (later movements excluded). */
  system_qty: qtyString,
  /** counted − system. */
  variance: qtyString,
  unit_cost_cents: cents.nullable(),
  /** variance × cost, half up; null without a cost. */
  value_cents: cents.nullable(),
  needs_approval: z.boolean(),
  movement_id: z.uuid().nullable(),
  /**
   * A later count of this row that was recorded first (B-035): this line was kept but not
   * adjusted, since the later count already reflects the shelf. Its variance is what was
   * seen, not a movement.
   */
  superseded_by: z.object({ id: z.uuid(), number: z.string() }).nullable(),
  /**
   * The count's approval was rejected and a correcting movement undid this line's adjustment
   * (B-087): its variance was booked, then reversed, so it no longer stands.
   */
  reversed: z.boolean(),
});

/** `GET /counts/:id` response. */
export const countDetailSchema = countSummarySchema.extend({
  note: z.string().nullable(),
  lines: z.array(countLineDetailSchema),
});
/** Response of `GET /counts/:id`. */
export type CountDetail = z.infer<typeof countDetailSchema>;

/**
 * `GET /counts` query: newest first; `from`/`to` are local days of `counted_at`;
 * `product`: counts with a line for that product.
 */
export const countsQuerySchema = z.object({
  branch: z.uuid().optional(),
  location: z.uuid().optional(),
  product: z.uuid().optional(),
  from: plainDate.optional(),
  to: plainDate.optional(),
  needs_approval: queryBool,
  limit,
  cursor,
});
/** Parsed `GET /counts` query. */
export type CountsQuery = z.infer<typeof countsQuerySchema>;

/** `GET /counts` response. */
export const countsResponseSchema = z.object({
  items: z.array(countSummarySchema),
  next_cursor: z.string().nullable(),
});
/** Response of `GET /counts`. */
export type CountsResponse = z.infer<typeof countsResponseSchema>;

// ---------------------------------------------------------------------------------------
// Write-offs

/** A write-off. `reversed`: a manager rejected it and the stock went back on the books. */
export const writeOffSchema = z.object({
  id: z.uuid(),
  number: z.string(),
  branch: branchRefSchema,
  location: stockLocationRefSchema,
  product: stockProductRefSchema,
  lot: lotRefSchema.nullable(),
  state: stateKeySchema,
  qty: qtyString,
  reason: z.enum(WRITE_OFF_REASONS),
  note: z.string().nullable(),
  unit_cost_cents: cents.nullable(),
  value_cents: cents.nullable(),
  status: z.enum(['applied', 'reversed']),
  needs_approval: z.boolean(),
  approval: approvalRefSchema.nullable(),
  recorded_by: personRefSchema.nullable(),
  occurred_at: instant,
  trading_day: plainDate,
});
/** A write-off. */
export type WriteOff = z.infer<typeof writeOffSchema>;

/** `GET /write-offs` query: newest first; `from`/`to` are trading days. */
export const writeOffsQuerySchema = z.object({
  branch: z.uuid().optional(),
  from: plainDate.optional(),
  to: plainDate.optional(),
  reason: z.enum(WRITE_OFF_REASONS).optional(),
  product: z.uuid().optional(),
  status: z.enum(['applied', 'reversed']).optional(),
  limit,
  cursor,
});
/** Parsed `GET /write-offs` query. */
export type WriteOffsQuery = z.infer<typeof writeOffsQuerySchema>;

/** `GET /write-offs` response. */
export const writeOffsResponseSchema = z.object({
  items: z.array(writeOffSchema),
  next_cursor: z.string().nullable(),
});
/** Response of `GET /write-offs`. */
export type WriteOffsResponse = z.infer<typeof writeOffsResponseSchema>;

/** How the write-off report groups. */
export const WRITE_OFF_REPORT_GROUPS = ['reason', 'product', 'day'] as const;

/** `GET /write-offs/report` query: trading days, inclusive. */
export const writeOffReportQuerySchema = z
  .object({
    from: plainDate,
    to: plainDate,
    group_by: z.enum(WRITE_OFF_REPORT_GROUPS).default('reason'),
    branch: z.uuid().optional(),
  })
  .refine((q) => q.from <= q.to, { message: '`from` must not be after `to`', path: ['to'] });
/** Parsed `GET /write-offs/report` query. */
export type WriteOffReportQuery = z.infer<typeof writeOffReportQuerySchema>;

/** One group of the write-off report. Reversed write-offs are left out. */
export const writeOffReportRowSchema = z.object({
  /** The reason code, product id or trading day. */
  key: z.string(),
  label: z.string(),
  count: count,
  /** Σ quantity: only when grouped by product (units differ between products). */
  qty: qtyString.nullable(),
  /** The product's base unit, with `qty` (by product only; else null). */
  unit: z.string().nullable(),
  /** Σ value of the costed write-offs. */
  value_cents: cents,
  /** Write-offs in the group without a cost (not in `value_cents`). */
  uncosted_count: count,
});

/** `GET /write-offs/report` response. */
export const writeOffReportSchema = z.object({
  from: plainDate,
  to: plainDate,
  group_by: z.enum(WRITE_OFF_REPORT_GROUPS),
  rows: z.array(writeOffReportRowSchema),
  total_count: count,
  total_value_cents: cents,
  uncosted_count: count,
});
/** Response of `GET /write-offs/report`. */
export type WriteOffReport = z.infer<typeof writeOffReportSchema>;

// ---------------------------------------------------------------------------------------
// Daily state list

/** A state task's status. `rolled_over`: not done that day; it carries on the next list. */
export const STATE_TASK_STATUSES = ['pending', 'done', 'skipped', 'rolled_over'] as const;

/** One task of a daily state list. */
export const stateTaskSchema = z.object({
  id: z.uuid(),
  branch_id: z.uuid(),
  due_on: plainDate,
  /** The first day it was listed (earlier than `due_on` when it rolled over). */
  first_due_on: plainDate,
  product: stockProductRefSchema,
  lot: lotRefSchema,
  location: stockLocationRefSchema,
  from_state: stateKeySchema,
  to_state: stateKeySchema,
  qty: qtyString,
  kind: z.enum(['forward', 'backward']),
  /**
   * A forward task to a state off sale for good (the rule's "Removed", "Expired"): the
   * stations write the stock off, moving it to `to_state` is the other choice (UI4S-205),
   * so the Admin says "Written off" too (UI8X-2).
   */
  write_off: z.boolean().default(false),
  overdue: z.boolean(),
  reason: z.string(),
  rule_name: z.string(),
  status: z.enum(STATE_TASK_STATUSES),
  closed_at: instant.nullable(),
  closed_by: personRefSchema.nullable(),
  close_note: z.string().nullable(),
});
/** A state task. */
export type StateTask = z.infer<typeof stateTaskSchema>;

/** `GET /state-tasks` query: `day` defaults to today's trading day. */
export const stateTasksQuerySchema = z.object({
  day: plainDate.optional(),
  branch: z.uuid().optional(),
  status: z.enum(STATE_TASK_STATUSES).optional(),
});
/** Parsed `GET /state-tasks` query. */
export type StateTasksQuery = z.infer<typeof stateTasksQuerySchema>;

/** One branch's list for the day. */
export const stateListSummarySchema = z.object({
  branch: branchRefSchema,
  /** When the list was made; null when it hasn't been made yet today. */
  generated_at: instant.nullable(),
  lots_with_stock: count,
  lots_evaluated: count,
  pending: count,
  done: count,
  skipped: count,
  rolled_over: count,
});

/** `GET /state-tasks` response. */
export const stateTasksResponseSchema = z.object({
  day: plainDate,
  branches: z.array(stateListSummarySchema),
  items: z.array(stateTaskSchema),
});
/** Response of `GET /state-tasks`. */
export type StateTasksResponse = z.infer<typeof stateTasksResponseSchema>;

/** `POST /state-tasks/generate` body. */
export const stateListGenerateRequestSchema = z.object({ branch_id: z.uuid() });

/** `POST /state-tasks/generate` response (also what the scheduled job does per branch). */
export const stateListGenerateResponseSchema = z.object({
  branch_id: z.uuid(),
  due_on: plainDate,
  /** Tasks added now. */
  created: count,
  /** Tasks the day's list already had. */
  existing: count,
  /** Earlier days' pending tasks closed as rolled over. */
  rolled_over: count,
  check: z.object({
    result: z.enum(['pass', 'fail', 'partial', 'could_not_run']),
    expected: count,
    checked: count,
  }),
});
/** Response of `POST /state-tasks/generate`. */
export type StateListGenerateResponse = z.infer<typeof stateListGenerateResponseSchema>;

// ---------------------------------------------------------------------------------------
// Settings

const settingCents = z.int().min(0).max(100_000_000);
const settingPct = z.number().gt(0).max(100);
const listTime = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'Must be HH:mm')
  .refine(isCutoff, { message: 'Must be a time from 00:00 to 23:59' });

/**
 * `GET /settings/stock-control`: approval thresholds (D-100), the state-list time and the
 * stations' auto sign-out (D-046, B-033).
 */
export const stockControlSettingsSchema = z.object({
  writeoff_min_cents: settingCents,
  count_adjust_pct: settingPct,
  count_adjust_min_cents: settingCents,
  /** Local time the daily state list is made for each branch. */
  state_list_time: listTime,
  /** Minutes without a touch before a station signs its person out; 0 = never. */
  station_auto_sign_out_minutes: z.int().min(0).max(MAX_STATION_AUTO_SIGN_OUT_MINUTES),
});
/** Stock-control settings. */
export type StockControlSettings = z.infer<typeof stockControlSettingsSchema>;

/** `PUT /settings/stock-control` body: only what changes. */
export const updateStockControlSettingsSchema = stockControlSettingsSchema
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: 'Nothing to change' });
/** Body of `PUT /settings/stock-control`. */
export type UpdateStockControlSettings = z.infer<typeof updateStockControlSettingsSchema>;
